import React, { useMemo, useState } from 'react';
import { useUserFactsDashboard } from '../../hooks/useUserFactsDashboard';
import { useUserFacts } from '../../hooks/useUserFacts';
import { PeriodSelector } from './PeriodSelector';
import { ClickableNumericFactChart } from './NumericFactDetailModal';
import { StringFactItem } from './StringFactsList';
import { cn } from '../../utils/cn';
import { getMetricKeys } from '../../utils/wellbeingDataProcessor';
import { 
  RefreshCw, 
  FileText, 
  AlertCircle,
  BarChart3,
  Plus,
  Check
} from 'lucide-react';

interface UserFactsDashboardProps {
  className?: string;
}

export const UserFactsDashboard: React.FC<UserFactsDashboardProps> = ({
  className
}) => {
  const {
    numericFacts,
    loading: dashboardLoading,
    error,
    period,
    setPeriod,
    refreshDashboard,
    clearError
  } = useUserFactsDashboard();

  const { facts, createFact, updateFact, deleteFact, loading: factsLoading } = useUserFacts();

  // Get wellbeing metric keys to filter wellbeing metrics
  const wellbeingKeys = useMemo(() => getMetricKeys(), []);

  // Separate wellbeing metrics (numeric) from all facts
  const wellbeingMetrics = useMemo(() => {
    return numericFacts.filter(fact => wellbeingKeys.includes(fact.key));
  }, [numericFacts, wellbeingKeys]);

  // Get all string facts from useUserFacts (not filtered by period) for "Your Information"
  // Exclude wellbeing metrics from this section
  const yourInformationFacts = useMemo(() => {
    // Filter to only string facts, exclude wellbeing metrics, and convert to ProcessedStringFact format
    const stringFacts = facts
      .filter(fact => typeof fact.value === 'string' && !wellbeingKeys.includes(fact.key))
      .map(fact => ({
        key: fact.key,
        displayName: fact.key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase()),
        currentValue: fact.value,
        lastUpdated: fact.updatedAt,
        version: 1 // We don't have version info in UserFact, using 1 as default
      }));

    // Sort to put wellbeing_additional_info first
    return stringFacts.sort((a, b) => {
      if (a.key === 'wellbeing_additional_info') return -1;
      if (b.key === 'wellbeing_additional_info') return 1;
      return 0;
    });
  }, [facts, wellbeingKeys]);

  const loading = dashboardLoading || factsLoading;

  // Create new fact modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFactKey, setNewFactKey] = useState('');
  const [newFactValue, setNewFactValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleRefresh = async () => {
    await refreshDashboard();
  };

  const handleUpdateFact = async (key: string, value: string) => {
    await updateFact(key, { value });
  };

  const handleDeleteFact = async (key: string) => {
    await deleteFact(key);
  };

  const handleCreateFact = async () => {
    if (!newFactKey.trim() || !newFactValue.trim()) return;

    setIsCreating(true);
    try {
      await createFact({
        key: newFactKey.trim(),
        value: newFactValue.trim()
      });
      setShowCreateModal(false);
      setNewFactKey('');
      setNewFactValue('');
    } catch (error) {
      console.error('Failed to create fact:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setNewFactKey('');
    setNewFactValue('');
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-rm-primary-700">
            Wellbeing Insights
          </h2>
          <p className="text-sm text-rm-neutral-600 mt-1">
            Track changes in your wellbeing metrics over time
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-rm-neutral-600">Period</span>
          <PeriodSelector
            value={period}
            onChange={setPeriod}
            disabled={loading}
          />
          <button
            onClick={handleRefresh}
            disabled={loading}
            className={cn(
              'p-2 rounded-lg border border-rm-neutral-600/30 text-rm-neutral-600',
              'hover:bg-rm-bg-50 hover:text-rm-ink-900 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            title="Refresh data"
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-card p-4 shadow-soft">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error loading dashboard</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={clearError}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && numericFacts.length === 0 && yourInformationFacts.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-rm-accent-600"></div>
          <p className="mt-3 text-sm text-rm-neutral-600">Loading dashboard data...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && wellbeingMetrics.length === 0 && yourInformationFacts.length === 0 && !error && (
        <div className="text-center py-12 bg-white border border-rm-neutral-600/20 rounded-card shadow-soft">
          <BarChart3 className="w-12 h-12 mx-auto text-rm-neutral-600 mb-3" />
          <h3 className="text-base font-medium text-rm-ink-900">No data yet</h3>
          <p className="mt-2 text-sm text-rm-neutral-600 max-w-md mx-auto">
            Start conversations with the wellbeing officer to collect insights about your preferences and wellbeing metrics.
          </p>
        </div>
      )}

      {/* Wellbeing Metrics Section */}
      {wellbeingMetrics.length > 0 && (
        <section className="mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {wellbeingMetrics.map((fact) => (
              <ClickableNumericFactChart 
                key={fact.key} 
                fact={fact} 
                compact 
              />
            ))}
          </div>
        </section>
      )}

      {/* Your Information Section */}
      {yourInformationFacts.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-rm-primary-700" />
              <h3 className="text-base font-semibold text-rm-primary-700">
                Your Information
              </h3>
              <span className="text-xs text-rm-neutral-600 bg-rm-bg-50 px-2 py-0.5 rounded-full">
                {yourInformationFacts.length}
              </span>
            </div>
            
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-rm-accent-600 hover:text-rm-accent-700 hover:bg-rm-accent-50 rounded-lg transition-colors border border-rm-accent-300"
              title="Add new metric"
            >
              <Plus className="w-3 h-3" />
              Add
            </button>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {yourInformationFacts.map((fact) => (
                <StringFactItem 
                  key={fact.key} 
                  fact={fact} 
                  onUpdate={handleUpdateFact}
                  onDelete={handleDeleteFact}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Create New Fact Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-rm-primary-700 mb-4">
              Add New Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-rm-ink-900 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newFactKey}
                  onChange={(e) => setNewFactKey(e.target.value)}
                  placeholder="e.g., favorite_color"
                  className="w-full px-3 py-2 border border-rm-neutral-600/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-accent-500"
                  disabled={isCreating}
                  autoFocus
                />
                <p className="mt-1 text-xs text-rm-neutral-600">
                  Use lowercase with underscores (e.g., favorite_color)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-rm-ink-900 mb-1">
                  Value
                </label>
                <textarea
                  value={newFactValue}
                  onChange={(e) => setNewFactValue(e.target.value)}
                  placeholder="Enter the value..."
                  rows={3}
                  className="w-full px-3 py-2 border border-rm-neutral-600/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-accent-500 resize-none"
                  disabled={isCreating}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                onClick={handleCancelCreate}
                disabled={isCreating}
                className="px-4 py-2 text-sm font-medium text-rm-neutral-600 hover:text-rm-ink-900 border border-rm-neutral-600/30 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFact}
                disabled={isCreating || !newFactKey.trim() || !newFactValue.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-rm-accent-600 hover:bg-rm-accent-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Create
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFactsDashboard;
